import React, { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { AuthPage } from '../../pages/AuthPage'
import { navigateTo } from '../../services/navigation'
import { useI18n } from '../../i18n/I18nContext'

interface AuthGuardProps {
  children: React.ReactNode
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  const { t } = useI18n()
  const [expired, setExpired] = useState(false)
  // The full-screen loader is only for the initial session probe. After the
  // first load completes, login/MFA submissions toggle `loading` too, and
  // unmounting the auth UI for those would discard transient state such as the
  // MFA challenge token, pending enrollment, and recovery codes. Keep the auth
  // UI mounted (it shows its own inline spinners) once bootstrapped.
  const [hasBootstrapped, setHasBootstrapped] = useState(false)

  useEffect(() => {
    if (!loading) {
      setHasBootstrapped(true)
      setExpired(false)
      return
    }
    const timer = window.setTimeout(() => setExpired(true), 4000)
    return () => window.clearTimeout(timer)
  }, [loading])

  if (loading && !hasBootstrapped && !expired) {
    return (
      <div className="auth-layout">
        <div className="panel" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p className="text-muted">{t('Loading workspace…')}</p>
        </div>
      </div>
    )
  }

  if (typeof window !== 'undefined' && /^\/org-invites\/[^/]+\/?$/.test(window.location.pathname)) return <AuthPage />

  if (!isAuthenticated) {
    if (typeof window !== 'undefined' && window.location.pathname.replace(/\/+$/, '') !== '/login' && !window.location.pathname.startsWith('/org-invites/')) {
      const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
      navigateTo(`/login?next=${encodeURIComponent(next)}`, { replace: true, notify: false })
    }
    return <AuthPage />
  }

  return <>{children}</>
}
